/**
 * Tagesauswertung ueber alle Gewohnheiten (Roadmap F-08).
 *
 * Gezaehlt wird nur, was an einem Tag VORGESEHEN ist: eine Mo/Mi/Fr-
 * Gewohnheit zaehlt am Dienstag weder als geschafft noch als offen.
 * Wochenziele ("3x pro Woche") haben keinen festen Tag und zaehlen deshalb
 * nie zur Tagesquote - sonst muesste man sie taeglich erledigen, um einen
 * perfekten Tag zu haben. Sie haben ihren eigenen Wochenstand.
 *
 * Wie bei den Streaks: alles wird berechnet, nichts gespeichert.
 */
import { isScheduled } from '@/domain/habits/streak';
import { addDays, startOfWeek, toLocalDate } from '@/lib/datetime';
import type { Habit, HabitCadence, LocalDate } from '@/domain/types';

/** Eine Gewohnheit mit den Tagen, an denen sie erledigt wurde. */
export interface HabitDays {
  habit: Habit;
  done: Set<LocalDate>;
}

export interface TagesAuswertung {
  erledigt: Habit[];
  offen: Habit[];
  /** 0..1, `null` wenn an dem Tag nichts vorgesehen ist. */
  quote: number | null;
  /** Nur wahr, wenn etwas vorgesehen war und alles erledigt ist. */
  alleGeschafft: boolean;
}

/** An diesem Tag fest vorgesehen? Wochenziele nie. */
export function vorgesehenAm(date: LocalDate, cadence: HabitCadence): boolean {
  return cadence.type !== 'x_pro_woche' && isScheduled(date, cadence);
}

/** Vor dem Anlegen gab es die Gewohnheit nicht - rueckwirkend zaehlt sie nicht. */
function gabEsSchon(habit: Habit, date: LocalDate): boolean {
  if (!habit.createdAt) return true;
  const seit = toLocalDate(new Date(habit.createdAt));
  return date >= seit;
}

export function tagesAuswertung(eintraege: HabitDays[], date: LocalDate): TagesAuswertung {
  const erledigt: Habit[] = [];
  const offen: Habit[] = [];
  for (const { habit, done } of eintraege) {
    if (habit.archived || !vorgesehenAm(date, habit.cadence) || !gabEsSchon(habit, date)) continue;
    (done.has(date) ? erledigt : offen).push(habit);
  }
  const summe = erledigt.length + offen.length;
  return {
    erledigt,
    offen,
    quote: summe === 0 ? null : erledigt.length / summe,
    alleGeschafft: summe > 0 && offen.length === 0,
  };
}

export interface TagesPunkt {
  date: LocalDate;
  quote: number | null;
  erledigt: number;
  vorgesehen: number;
}

/** Tagesquote der letzten `tage` Tage, aeltester zuerst. */
export function tagesVerlauf(eintraege: HabitDays[], heute: LocalDate, tage: number): TagesPunkt[] {
  const punkte: TagesPunkt[] = [];
  for (let i = tage - 1; i >= 0; i -= 1) {
    const date = addDays(heute, -i);
    const a = tagesAuswertung(eintraege, date);
    punkte.push({ date, quote: a.quote, erledigt: a.erledigt.length, vorgesehen: a.erledigt.length + a.offen.length });
  }
  return punkte;
}

/**
 * Perfekte Tage in Folge (alles Vorgesehene geschafft).
 *
 * Schonfrist wie bei den Streaks: ein heute noch offener Tag reisst die Serie
 * nicht. Tage ohne Vorgesehenes werden uebersprungen. Gesucht wird hoechstens
 * `fenster` Tage zurueck - so weit reichen die geladenen Daten; `mindestens`
 * sagt, dass die Serie dort noch nicht zu Ende war.
 */
export function perfekteTageSerie(
  eintraege: HabitDays[],
  heute: LocalDate,
  fenster: number,
): { tage: number; mindestens: boolean } {
  let tage = 0;
  for (let i = 0; i < fenster; i += 1) {
    const a = tagesAuswertung(eintraege, addDays(heute, -i));
    if (a.quote === null) continue;
    if (a.alleGeschafft) { tage += 1; continue; }
    if (i === 0) continue; // heute laeuft noch
    return { tage, mindestens: false };
  }
  return { tage, mindestens: tage > 0 };
}

/** Wie oft ein Wochenziel in der laufenden Woche (ab Montag) erledigt ist. */
export function wochenStand(done: Set<LocalDate>, heute: LocalDate): number {
  const start = startOfWeek(heute);
  let n = 0;
  for (let d = start; d <= heute; d = addDays(d, 1)) if (done.has(d)) n += 1;
  return n;
}
